/**
 * The active wallet: one of the signed-in Privy user's Solana wallets (the embedded wallet, or Phantom, Solflare,
 * Backpack...), or on the local clone in development builds the Dev wallet. Every signer signs with
 * signTransaction only (never signAndSend) and re-checks the signing guard first.
 */
import { useCallback, useMemo } from 'react';
import { usePrivy, type WalletWithMetadata } from '@privy-io/react-auth';
import { useSignTransaction, useWallets } from '@privy-io/react-auth/solana';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useChain } from '@/chain/ChainProvider';
import { assertSigningAllowed, SigningRefusedError } from '@/chain/guard';
import { safeStorage } from '@/store/storage';
import type { TxSigner } from '@/tx/send';
import { devWalletAllowed, stopDevWallet, useDevSession } from './devSession';

interface Preferred {
  address: string | null;
  set: (address: string | null) => void;
}

/** The wallet the user picked in the menu (this browser only). */
const usePreferred = create<Preferred>()(
  persist((set) => ({ address: null, set: (address) => set({ address }) }), { name: 'raydius.wallet.v1', storage: safeStorage, partialize: (s) => ({ address: s.address }) as Preferred }),
);

export interface WalletOption {
  address: string;
  label: string;
  embedded: boolean;
}

export interface ActiveWallet {
  /** Privy has loaded: show signed-in or signed-out UI only once this is true. */
  ready: boolean;
  authenticated: boolean;
  kind: 'privy' | 'dev' | null;
  address: string | null;
  label: string;
  embedded: boolean;
  /** Signed in, but no usable wallet yet (the embedded wallet is being created, or an external wallet is locked). */
  pendingWallet: boolean;
  /** Signed in with an external wallet that is not connected right now (locked, or a reload): connect() asks for it. */
  needsConnect: boolean;
  connect: () => void;
  signer: TxSigner | null;
  options: WalletOption[];
  select: (address: string) => void;
  login: () => void;
  logout: () => Promise<void>;
  /** The X username on the Privy account, if one is linked there. */
  twitter: string | null;
}

const isSolanaWallet = (a: { type: string }): a is WalletWithMetadata => a.type === 'wallet' && (a as WalletWithMetadata).chainType === 'solana';
const isEmbedded = (a: WalletWithMetadata | undefined) => a?.walletClientType === 'privy' || a?.walletClientType === 'privy-v2';

export function useActiveWallet(): ActiveWallet {
  const { config, verdict } = useChain();
  const { ready, authenticated, user, login, logout, connectWallet } = usePrivy();
  const { wallets } = useWallets();
  const { signTransaction } = useSignTransaction();
  const preferred = usePreferred((s) => s.address);
  const setPreferred = usePreferred((s) => s.set);
  const devSigner = useDevSession((s) => s.signer);
  // import.meta.env.DEV first, so production builds drop the Dev wallet branch entirely.
  const dev = import.meta.env.DEV && devWalletAllowed(config.cluster) ? devSigner : null;

  const signOut = useCallback(async () => {
    stopDevWallet();
    if (authenticated) await logout();
  }, [authenticated, logout]);

  return useMemo<ActiveWallet>(() => {
    const base = { ready, authenticated, login: () => login(), logout: signOut, select: setPreferred, twitter: user?.twitter?.username ?? null, connect: () => connectWallet({ walletChainType: 'solana-only' }), needsConnect: false };
    if (import.meta.env.DEV && dev) {
      const signer: TxSigner = {
        address: dev.address,
        async signTransaction(tx) {
          assertSigningAllowed(verdict);
          if (verdict.cluster !== 'local') throw new SigningRefusedError('mismatch', 'The Dev wallet signs on the local clone only.');
          return dev.signTransaction(tx);
        },
      };
      return { ...base, ready: true, kind: 'dev', address: dev.address, label: 'Dev wallet', embedded: false, pendingWallet: false, signer, options: [] };
    }
    const linked = new Map((user?.linkedAccounts ?? []).filter(isSolanaWallet).map((a) => [a.address, a]));
    const usable = authenticated ? wallets.filter((w) => linked.has(w.address)) : [];
    const options: WalletOption[] = usable.map((w) => {
      const embedded = isEmbedded(linked.get(w.address));
      return { address: w.address, label: embedded ? 'Raydius wallet' : w.standardWallet.name || 'Wallet', embedded };
    });
    const pick = usable.find((w) => w.address === preferred) ?? usable.find((w) => isEmbedded(linked.get(w.address))) ?? usable[0];
    if (!pick) {
      const external = authenticated && linked.size > 0 && ![...linked.values()].some(isEmbedded);
      return { ...base, kind: null, address: null, label: '', embedded: false, pendingWallet: authenticated && !external, needsConnect: external, signer: null, options };
    }
    const option = options.find((o) => o.address === pick.address)!;
    const signer: TxSigner = {
      address: pick.address,
      async signTransaction(tx) {
        assertSigningAllowed(verdict);
        // Embedded wallets sign without Privy's screen (it needs a browser RPC); the app shows the full cost first.
        const { signedTransaction } = await signTransaction({ transaction: tx, wallet: pick, chain: verdict.chain, options: { uiOptions: { showWalletUIs: false } } });
        return signedTransaction;
      },
    };
    return { ...base, kind: 'privy', address: pick.address, label: option.label, embedded: option.embedded, pendingWallet: false, signer, options };
  }, [ready, authenticated, login, connectWallet, signOut, setPreferred, user, dev, verdict, wallets, preferred, signTransaction]);
}
